import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Building2, Users, BarChart3, Target, Shield, Award,
  TrendingUp, Briefcase, LineChart, CheckCircle2, ArrowRight,
  PieChart, UserCheck, Settings, MessageSquare, Rocket, ClipboardCheck
} from 'lucide-react';
import { Button } from './ui/button';
import { useAudience } from '../context/AudienceContext';
import { openBookDemo } from '../utils/config';

const B2BValueSection = ({ onContactClick, onROICalculatorClick, onCaseStudiesClick }) => {
  const { t } = useTranslation();
  const { isB2B, hasSelected } = useAudience();

  // Only show if B2B is selected or no selection made
  if (hasSelected && !isB2B) return null;

  const features = [
    {
      icon: Users,
      title: t('b2bValue.features.teamDev.title', 'Team Development at Scale'),
      description: t('b2bValue.features.teamDev.description', 'Deploy coaching programs across departments, regions, and leadership levels with centralized management.'),
      color: 'blue'
    },
    {
      icon: BarChart3,
      title: t('b2bValue.features.analytics.title', 'Real-Time ROI Analytics'),
      description: t('b2bValue.features.analytics.description', 'Track engagement, progress, and business impact with comprehensive dashboards and automated reporting.'),
      color: 'indigo'
    },
    {
      icon: Shield,
      title: t('b2bValue.features.enterprise.title', 'Enterprise-Grade Security'),
      description: t('b2bValue.features.enterprise.description', 'SOC 2 compliant platform with SSO integration, data encryption, and dedicated account support.'),
      color: 'purple'
    },
    {
      icon: UserCheck,
      title: t('b2bValue.features.matching.title', 'AI-Powered Coach Matching'),
      description: t('b2bValue.features.matching.description', 'Our algorithm pairs employees with certified coaches based on goals, learning style, and expertise needs.'),
      color: 'cyan'
    },
    {
      icon: PieChart,
      title: t('b2bValue.features.customization.title', 'Custom Program Design'),
      description: t('b2bValue.features.customization.description', 'Tailored coaching curricula aligned with your organizational goals, values, and competency frameworks.'),
      color: 'teal'
    },
    {
      icon: Settings,
      title: t('b2bValue.features.integration.title', 'HRIS Integration'),
      description: t('b2bValue.features.integration.description', 'Seamless integration with your existing HR systems for automated onboarding and progress syncing.'),
      color: 'blue'
    }
  ];

  const stats = [
    { value: '65%', label: t('b2bValue.stats.profitability', 'Increased Profitability'), source: 'ICF/HCI 2019' },
    { value: '62%', label: t('b2bValue.stats.retention', 'Higher Talent Retention'), source: 'ICF/HCI 2019' },
    { value: '3.5x', label: t('b2bValue.stats.roi', 'Average ROI'), source: 'ICF Global 2020' },
    { value: '85%', label: t('b2bValue.stats.satisfaction', 'Employee Satisfaction'), source: 'Internal Data' },
  ];

  const howItWorksSteps = [
    {
      number: '01',
      icon: MessageSquare,
      title: t('b2bValue.howItWorks.step1.title', 'Discovery Call'),
      description: t('b2bValue.howItWorks.step1.description', 'We learn about your organization\'s goals, challenges, and culture to design the perfect coaching program.')
    },
    {
      number: '02',
      icon: ClipboardCheck,
      title: t('b2bValue.howItWorks.step2.title', 'Custom Program Design'),
      description: t('b2bValue.howItWorks.step2.description', 'Our experts create a tailored coaching curriculum aligned with your competency frameworks and KPIs.')
    },
    {
      number: '03',
      icon: UserCheck,
      title: t('b2bValue.howItWorks.step3.title', 'Coach Matching'),
      description: t('b2bValue.howItWorks.step3.description', 'AI-powered matching pairs each participant with the ideal certified coach based on their needs.')
    },
    {
      number: '04',
      icon: Rocket,
      title: t('b2bValue.howItWorks.step4.title', 'Launch & Track'),
      description: t('b2bValue.howItWorks.step4.description', 'Roll out coaching across your organization with real-time analytics and ROI tracking dashboards.')
    }
  ];

  const colorClasses = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
    indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
    cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', border: 'border-cyan-200' },
    teal: { bg: 'bg-teal-100', text: 'text-teal-600', border: 'border-teal-200' },
  };

  return (
    <section className="py-20 bg-gradient-to-b from-blue-50 to-white" id="b2b-value">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Building2 className="w-4 h-4" />
            {t('b2bValue.badge', 'Enterprise Solutions')}
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {t('b2bValue.title', 'Transform Your Organization')}
          </h2>
          <p className="text-lg text-gray-600">
            {t('b2bValue.subtitle', 'Empower your leaders with scalable coaching programs that deliver measurable business impact.')}
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto mb-16">
          {stats.map((stat, idx) => (
            <div key={stat.label || `b2b-stat-${idx}`} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow">
              <div className="text-3xl lg:text-4xl font-bold text-blue-600 mb-1">{stat.value}</div>
              <div className="text-sm font-medium text-gray-900 mb-1">{stat.label}</div>
              <div className="text-xs text-gray-500">{stat.source}</div>
            </div>
          ))}
        </div>

        {/* How It Works Section */}
        <div className="max-w-5xl mx-auto mb-20">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-12">
            {t('b2bValue.howItWorks.title', 'How It Works')}
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorksSteps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={`b2b-step-${idx}`} className="relative text-center">
                  {/* Step Number Circle */}
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg relative z-10">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  {/* Step Number Badge */}
                  <div className="absolute top-0 right-1/2 transform translate-x-10 -translate-y-2 w-7 h-7 bg-blue-800 rounded-full flex items-center justify-center text-xs font-bold text-white shadow">
                    {step.number}
                  </div>
                  {/* Content */}
                  <h4 className="font-bold text-gray-900 mb-2">{step.title}</h4>
                  <p className="text-sm text-gray-600">{step.description}</p>
                  {/* Connector Line (not on last item) */}
                  {idx < howItWorksSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-blue-200" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16">
          {features.map((feature, idx) => {
            const Icon = feature.icon;
            const colors = colorClasses[feature.color];
            return (
              <div key={feature.title || `b2b-benefit-${idx}`} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center mb-4`}>
                  <Icon className={`w-6 h-6 ${colors.text}`} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* CTAs */}
        <div className="text-center space-y-4">
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold shadow-lg rounded-xl"
              onClick={openBookDemo}
              data-testid="b2b-book-demo-btn"
            >
              {t('b2bValue.cta.demo', 'Book a Demo')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg font-semibold rounded-xl"
              onClick={onROICalculatorClick}
              data-testid="b2b-roi-btn"
            >
              <LineChart className="mr-2 h-5 w-5" />
              {t('b2bValue.cta.roi', 'Calculate Your ROI')}
            </Button>
          </div>
          <button 
            onClick={onCaseStudiesClick}
            className="text-blue-600 hover:text-blue-700 font-medium text-sm inline-flex items-center gap-1"
          >
            {t('b2bValue.cta.caseStudies', 'View Success Stories')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default B2BValueSection;
